import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as MdIcons from 'react-icons/md';

const { MdClose, MdSave, MdVpnKey, MdSmartToy, MdCheck } = MdIcons;

const SettingsModal = ({ isOpen, onClose }) => {
  const [apiKey, setApiKey] = useState('');
  const [model, setModel] = useState('google/gemini-1.5-flash');
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const storedKey = localStorage.getItem('openrouter_api_key') || '';
      // Default to 1.5 Flash if nothing stored, as it's most stable for audio
      const storedModel = localStorage.getItem('selected_model') || 'google/gemini-1.5-flash';
      setApiKey(storedKey);
      setModel(storedModel);
    }
  }, [isOpen]);

  const handleSave = () => {
    localStorage.setItem('openrouter_api_key', apiKey);
    localStorage.setItem('selected_model', model);
    setShowSuccess(true);
    setTimeout(() => {
      setShowSuccess(false);
      onClose();
    }, 1000);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={onClose}
        />
        <motion.div 
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative w-full max-w-md bg-white dark:bg-surface-dark border border-slate-200 dark:border-border-dark rounded-2xl shadow-2xl overflow-hidden"
        >
          <div className="p-6 border-b border-slate-200 dark:border-border-dark flex justify-between items-center">
            <h3 className="text-xl font-bold text-slate-900 dark:text-white">Settings</h3>
            <button 
              onClick={onClose}
              className="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-white/10 text-slate-500 dark:text-slate-400 transition-colors"
            >
              <SafeIcon icon={MdClose} className="text-xl" />
            </button>
          </div>

          <div className="p-6 space-y-6">
            {/* API Key Input */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-300">
                <SafeIcon icon={MdVpnKey} className="text-primary" />
                OpenRouter API Key
              </label>
              <input 
                type="password" 
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="sk-or-..." 
                className="w-full px-4 py-3 rounded-xl bg-slate-50 dark:bg-background-dark border border-slate-200 dark:border-border-dark focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all text-slate-900 dark:text-white"
              />
              <p className="text-xs text-slate-500 dark:text-text-secondary">
                Your key is stored locally in your browser.
              </p>
            </div>

            {/* Model Selection (Input + Datalist) */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-300">
                <SafeIcon icon={MdSmartToy} className="text-primary" />
                AI Model
              </label>
              <div className="relative">
                <input 
                  type="text" 
                  list="model-suggestions" 
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                  placeholder="google/gemini-1.5-flash"
                  className="w-full px-4 py-3 rounded-xl bg-slate-50 dark:bg-background-dark border border-slate-200 dark:border-border-dark focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all text-slate-900 dark:text-white"
                />
                <datalist id="model-suggestions">
                   <option value="google/gemini-1.5-flash" label="Recommended for Audio (Stable)" />
                   <option value="google/gemini-2.0-flash-001" label="Experimental (New)" />
                   <option value="google/gemini-pro-1.5" label="High Intelligence (Slower)" />
                   <option value="openai/gpt-4o" label="GPT-4o (Expensive)" />
                </datalist>
              </div>
              <p className="text-xs text-slate-500 dark:text-text-secondary">
                <b>Recommendation:</b> Use <code>gemini-1.5-flash</code> for best audio compatibility.
              </p>
            </div>
          </div>

          <div className="p-6 pt-0">
            <button 
              onClick={handleSave}
              className={`w-full py-3 rounded-xl font-bold text-white flex items-center justify-center gap-2 transition-all ${showSuccess ? 'bg-green-500 hover:bg-green-600' : 'bg-primary hover:bg-primary/90'}`}
            >
              {showSuccess ? (
                <>
                  <SafeIcon icon={MdCheck} className="text-xl" />
                  Saved!
                </>
              ) : (
                <>
                  <SafeIcon icon={MdSave} className="text-xl" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default SettingsModal;